import { theme as antdTheme } from 'antd';
import type { ThemeConfig } from 'antd';

/** 万剪师后台统一设计令牌。业务页面不再自行定义主题颜色和控件尺寸。 */
export const adminTheme: ThemeConfig = {
  token: {
    colorPrimary: '#2563eb',
    colorInfo: '#2563eb',
    colorSuccess: '#16a34a',
    colorWarning: '#d97706',
    colorError: '#dc2626',
    colorText: '#1e293b',
    colorTextSecondary: '#64748b',
    colorTextTertiary: '#94a3b8',
    colorBgLayout: '#f3f5f8',
    colorBgContainer: '#ffffff',
    colorFillAlter: '#f8fafc',
    colorBorder: '#dfe4ea',
    colorBorderSecondary: '#edf0f3',
    borderRadius: 6,
    borderRadiusLG: 8,
    controlHeight: 36,
    controlHeightSM: 30,
    fontSize: 14,
    fontSizeHeading3: 22,
    lineWidth: 1,
    boxShadow: '0 8px 24px rgba(15, 23, 42, 0.08)',
    boxShadowSecondary: '0 3px 12px rgba(15, 23, 42, 0.06)',
  },
  components: {
    Alert: {
      borderRadiusLG: 8,
    },
    Button: {
      borderRadius: 6,
      defaultShadow: 'none',
      dangerShadow: 'none',
      primaryShadow: 'none',
      fontWeight: 500,
      paddingInline: 15,
    },
    Card: {
      borderRadiusLG: 8,
      bodyPadding: 20,
      headerHeight: 52,
      headerFontSize: 15,
    },
    DatePicker: {
      activeBorderColor: '#2563eb',
      hoverBorderColor: '#60a5fa',
      activeShadow: '0 0 0 2px rgba(37, 99, 235, 0.10)',
    },
    Drawer: {
      colorBgElevated: '#ffffff',
    },
    Form: {
      labelColor: '#475569',
      labelFontSize: 13,
      itemMarginBottom: 18,
    },
    Input: {
      activeBorderColor: '#2563eb',
      hoverBorderColor: '#60a5fa',
      activeShadow: '0 0 0 2px rgba(37, 99, 235, 0.10)',
    },
    InputNumber: {
      activeBorderColor: '#2563eb',
      hoverBorderColor: '#60a5fa',
      activeShadow: '0 0 0 2px rgba(37, 99, 235, 0.10)',
    },
    Layout: {
      bodyBg: '#f3f5f8',
      headerBg: '#ffffff',
      siderBg: '#ffffff',
    },
    Menu: {
      itemBg: 'transparent',
      subMenuItemBg: 'transparent',
      itemColor: '#667085',
      itemHoverBg: '#f3f7ff',
      itemHoverColor: '#2563eb',
      itemSelectedBg: '#eaf2ff',
      itemSelectedColor: '#2563eb',
      itemActiveBg: '#edf4ff',
      itemHeight: 44,
      itemBorderRadius: 11,
      itemMarginInline: 9,
      itemMarginBlock: 4,
    },
    Modal: {
      borderRadiusLG: 10,
      titleFontSize: 17,
    },
    Pagination: {
      itemActiveBg: '#eff6ff',
      itemBg: 'transparent',
      itemSize: 32,
      itemSizeSM: 28,
    },
    Radio: {
      buttonCheckedBg: '#eff6ff',
      buttonSolidCheckedBg: '#2563eb',
    },
    Select: {
      activeBorderColor: '#2563eb',
      hoverBorderColor: '#60a5fa',
      activeOutlineColor: 'rgba(37, 99, 235, 0.10)',
      optionSelectedBg: '#eff6ff',
    },
    Switch: {
      colorPrimary: '#2563eb',
      colorPrimaryHover: '#1d4ed8',
    },
    Table: {
      borderColor: '#e8edf3',
      headerBg: '#f6f8fb',
      headerColor: '#526176',
      headerSplitColor: '#e5eaf0',
      rowHoverBg: '#f4f8ff',
      rowSelectedBg: '#eef5ff',
      rowSelectedHoverBg: '#e8f2ff',
      cellPaddingBlock: 14,
      cellPaddingInline: 16,
    },
    Tabs: {
      inkBarColor: '#2563eb',
      itemActiveColor: '#1d4ed8',
      itemHoverColor: '#2563eb',
      itemSelectedColor: '#2563eb',
    },
    Tag: {
      borderRadiusSM: 4,
    },
    Upload: {
      colorFillAlter: '#f8fafc',
    },
  },
};

/** 深色模式使用中性炭黑底色，保留蓝色作为操作与选中态。 */
export const adminDarkTheme: ThemeConfig = {
  algorithm: antdTheme.darkAlgorithm,
  token: {
    colorPrimary: '#5b8def',
    colorInfo: '#5b8def',
    colorSuccess: '#3fb950',
    colorWarning: '#d29922',
    colorError: '#f85149',
    colorText: '#e6edf3',
    colorTextSecondary: '#9aa7b7',
    colorTextTertiary: '#6e7d90',
    colorBgBase: '#0e1013',
    colorBgLayout: '#0b0d10',
    colorBgContainer: '#17191e',
    colorBgElevated: '#1d2026',
    colorFillAlter: '#1c1f25',
    colorBorder: '#32363e',
    colorBorderSecondary: '#272b32',
    borderRadius: 6,
    borderRadiusLG: 8,
    controlHeight: 36,
    controlHeightSM: 30,
    fontSize: 14,
    fontSizeHeading3: 22,
    lineWidth: 1,
    boxShadow: '0 12px 30px rgba(0, 0, 0, 0.34)',
    boxShadowSecondary: '0 5px 16px rgba(0, 0, 0, 0.26)',
  },
  components: {
    Alert: {
      borderRadiusLG: 8,
    },
    Button: {
      borderRadius: 6,
      defaultShadow: 'none',
      dangerShadow: 'none',
      primaryShadow: 'none',
      fontWeight: 500,
      paddingInline: 15,
    },
    Card: {
      borderRadiusLG: 8,
      bodyPadding: 20,
      headerHeight: 52,
      headerFontSize: 15,
    },
    DatePicker: {
      activeBorderColor: '#5b8def',
      hoverBorderColor: '#7da2f5',
      activeShadow: '0 0 0 2px rgba(91, 141, 239, 0.18)',
    },
    Drawer: {
      colorBgElevated: '#1d2026',
    },
    Form: {
      labelColor: '#b4bfcc',
      labelFontSize: 13,
      itemMarginBottom: 18,
    },
    Input: {
      activeBorderColor: '#5b8def',
      hoverBorderColor: '#7da2f5',
      activeShadow: '0 0 0 2px rgba(91, 141, 239, 0.18)',
    },
    InputNumber: {
      activeBorderColor: '#5b8def',
      hoverBorderColor: '#7da2f5',
      activeShadow: '0 0 0 2px rgba(91, 141, 239, 0.18)',
    },
    Layout: {
      bodyBg: '#0b0d10',
      headerBg: '#17191e',
      siderBg: '#121419',
    },
    Menu: {
      itemBg: 'transparent',
      subMenuItemBg: 'transparent',
      itemColor: '#9aa7b7',
      itemHoverBg: '#22262d',
      itemHoverColor: '#79a3f5',
      itemSelectedBg: '#1d2c45',
      itemSelectedColor: '#79a3f5',
      itemActiveBg: '#21314a',
      itemHeight: 44,
      itemBorderRadius: 11,
      itemMarginInline: 9,
      itemMarginBlock: 4,
    },
    Modal: {
      borderRadiusLG: 10,
      titleFontSize: 17,
    },
    Pagination: {
      itemActiveBg: '#20304a',
      itemBg: 'transparent',
      itemSize: 32,
      itemSizeSM: 28,
    },
    Radio: {
      buttonCheckedBg: '#20304a',
      buttonSolidCheckedBg: '#5b8def',
    },
    Select: {
      activeBorderColor: '#5b8def',
      hoverBorderColor: '#7da2f5',
      activeOutlineColor: 'rgba(91, 141, 239, 0.18)',
      optionSelectedBg: '#20304a',
    },
    Switch: {
      colorPrimary: '#5b8def',
      colorPrimaryHover: '#79a3f5',
    },
    Table: {
      borderColor: '#263140',
      headerBg: '#1e2127',
      headerColor: '#aeb9c7',
      headerSplitColor: '#30343b',
      rowHoverBg: '#22262d',
      rowSelectedBg: '#1d2d46',
      rowSelectedHoverBg: '#213552',
      cellPaddingBlock: 14,
      cellPaddingInline: 16,
    },
    Tabs: {
      inkBarColor: '#5b8def',
      itemActiveColor: '#79a3f5',
      itemHoverColor: '#79a3f5',
      itemSelectedColor: '#79a3f5',
    },
    Tag: {
      borderRadiusSM: 4,
    },
    Upload: {
      colorFillAlter: '#1c1f25',
    },
  },
};
